import type { SavedMicChoice } from "./micPreference";

export interface MicDevice {
    deviceId: string;
    label: string; // already trimmed
    builtIn: boolean;
    browserDefault?: true; // the real twin of Chrome's "default" entry (set by dedupeAudioInputs)
}

const VIRTUAL_IDS = ["default", "communications"];

// Chrome prefixes the virtual entries ("Default - X") and some drivers append a "(0b0e:0422)" id.
export const trimMicLabel = (label: string): string =>
    label
        .trim()
        .replace(/^(default|communications)\s+-\s+/i, "")
        .replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, "")
        .trim();

export const isBuiltInMic = (label: string): boolean =>
    /built-in|internal|macbook|ipad|iphone|microphone array|realtek/i.test(label);

export const dedupeAudioInputs = (devices: MediaDeviceInfo[]): MicDevice[] => {
    const audio = devices.filter((d) => d.kind === "audioinput");
    const labelled = audio.some((d) => d.label.trim() !== "") ? audio.filter((d) => d.label.trim() !== "") : audio;
    const real = labelled.filter((d) => VIRTUAL_IDS.indexOf(d.deviceId) === -1);
    const realLabels = real.map((d) => trimMicLabel(d.label));
    const toMic = (d: MediaDeviceInfo): MicDevice => {
        const label = trimMicLabel(d.label);
        return { deviceId: d.deviceId, label, builtIn: isBuiltInMic(label) };
    };

    const result: MicDevice[] = [];
    const seen: Record<string, boolean> = {};
    const push = (m: MicDevice) => {
        if (seen[m.deviceId]) return;
        seen[m.deviceId] = true;
        result.push(m);
    };

    // When the default entry has a real twin, that twin goes first and is marked as the browser default.
    const defaultEntry = labelled.find((d) => d.deviceId === "default");
    if (defaultEntry) {
        const twin = real.find((d) => trimMicLabel(d.label) === trimMicLabel(defaultEntry.label));
        if (twin) push({ ...toMic(twin), browserDefault: true });
    }
    real.forEach((d) => push(toMic(d)));
    // A virtual entry with no real twin is the only handle on that mic, so keep it.
    labelled
        .filter((d) => VIRTUAL_IDS.indexOf(d.deviceId) !== -1)
        .forEach((d) => {
            if (realLabels.indexOf(trimMicLabel(d.label)) === -1) push(toMic(d));
        });
    return result;
};

export const resolveMicChoice = (choice: SavedMicChoice | null, inputs: MicDevice[]): MicDevice | null => {
    if (!choice) return null;
    const byId = inputs.find((d) => d.deviceId === choice.deviceId);
    if (byId) return byId;
    const wanted = trimMicLabel(choice.label);
    return inputs.find((d) => d.label === wanted) ?? null;
};

// The mic the browser itself would open, when the browser says so: Chrome's "default" entry (its real
// twin, or the entry itself when it has none). Firefox and Safari expose no default entry, so null.
// Only for the bar's label: a start with no saved choice asks for the browser default, never this id.
export const browserDefaultMic = (inputs: MicDevice[]): MicDevice | null =>
    inputs.find((d) => d.browserDefault || d.deviceId === "default") ?? null;
